import React, { useState } from 'react';
import { useAttendeeData } from '../../hooks/useAttendeeData';
import EventCard from '../../components/attendee/EventCard';
import EmptyState from '../../components/attendee/EmptyState';
import LoadingState from '../../components/attendee/LoadingState';
import { downloadICSFile } from '../../utils/attendeeUtils';

const MyEventsPage = () => {
  const { loading, error, upcomingRegistrations, pastRegistrations, savedEvents } = useAttendeeData();
  const [activeTab, setActiveTab] = useState('upcoming');

  const tabs = [
    { key: 'upcoming', label: 'Upcoming', count: upcomingRegistrations.length },
    { key: 'past', label: 'Past', count: pastRegistrations.length },
    { key: 'saved', label: 'Saved', count: savedEvents.length },
  ];

  if (loading) {
    return (
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded w-48 animate-pulse mb-6" />
        <LoadingState count={4} />
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <EmptyState
          title="Something went wrong"
          message="We couldn't load your events. Please try again."
          ctaText="Retry"
          ctaLink="/attendee/events"
        />
      </div>
    );
  }

  const handleDownloadICS = () => {
    const allEvents = [...upcomingRegistrations, ...pastRegistrations]
      .map(r => r.events)
      .filter(Boolean);
    downloadICSFile(allEvents);
  };

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white">
            My Events
          </h1>
          <p className="text-gray-500 dark:text-gray-400 mt-1">
            Manage your registrations, saved events, and history.
          </p>
        </div>
        {(upcomingRegistrations.length > 0 || pastRegistrations.length > 0) && (
          <button
            onClick={handleDownloadICS}
            className="hidden sm:inline-flex items-center px-4 py-2 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg text-sm font-medium hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
          >
            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
            </svg>
            Download .ics
          </button>
        )}
      </div>

      {/* Tabs */}
      <div className="flex gap-1 mb-6 border-b border-gray-200 dark:border-gray-700">
        {tabs.map(tab => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
              activeTab === tab.key
                ? 'border-primary text-primary'
                : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
            }`}
          >
            {tab.label}
            <span className="ml-1.5 text-xs text-gray-400">({tab.count})</span>
          </button>
        ))}
      </div>

      {/* Tab content */}
      {activeTab === 'upcoming' && (
        <div className="space-y-3">
          {upcomingRegistrations.length > 0 ? (
            upcomingRegistrations.map(reg => (
              <EventCard key={reg.id} event={reg.events} registration={reg} />
            ))
          ) : (
            <EmptyState
              title="No upcoming events"
              message="You don't have any upcoming events yet."
              ctaText="Browse Events"
              ctaLink="/events"
            />
          )}
        </div>
      )}

      {activeTab === 'past' && (
        <div className="space-y-3">
          {pastRegistrations.length > 0 ? (
            pastRegistrations.map(reg => (
              <EventCard key={reg.id} event={reg.events} registration={reg} showActions={false} />
            ))
          ) : (
            <EmptyState
              title="No past events"
              message="Your past events will appear here after they conclude."
            />
          )}
        </div>
      )}

      {activeTab === 'saved' && (
        <div className="space-y-3">
          {savedEvents.length > 0 ? (
            savedEvents.map(event => (
              <EventCard key={event.id} event={event} />
            ))
          ) : (
            <EmptyState
              title="No saved events"
              message="Save events you're interested in and they'll appear here."
              ctaText="Explore Events"
              ctaLink="/events"
            />
          )}
        </div>
      )}
    </div>
  );
};

export default MyEventsPage;
